import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { executionPlanApi } from '../services/api';
import socketService from '../services/socket';
import Dialog from '../components/Dialog';
import './LiveExecutionPlanProgress.css';

/**
 * LiveExecutionPlanProgress Page
 * 
 * Display real-time progress of execution plan execution
 */
function LiveExecutionPlanProgress() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [execution, setExecution] = useState(null);
    const [goalExecutions, setGoalExecutions] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        loadExecution();

        // Set up Socket.io for real-time updates
        const socket = socketService.connect();

        if (socket) {
            console.log('[LiveExecutionPlanProgress] Setting up Socket.io listeners');

            // Join workspace room
            const workspaceId = localStorage.getItem('workspaceId');
            if (workspaceId) {
                socketService.joinWorkspace(workspaceId);
                console.log('[LiveExecutionPlanProgress] Joined workspace:', workspaceId);
            }

            // Listen for execution plan updates
            const handlePlanUpdate = (data) => {
                console.log('[LiveExecutionPlanProgress] Received update:', data);

                // Only process updates for this execution
                if (data.planExecutionId === id) {
                    // Reload execution data to get latest state
                    loadExecution();

                    // Handle completion/error - redirect after delay
                    // if (data.status === 'completed' || data.status === 'error') {
                    //     setTimeout(() => {
                    //         if (data.executionPlanId) {
                    //             navigate(`/execution-plans/${data.executionPlanId}`);
                    //         }
                    //     }, 3000);
                    // }
                }
            };

            socket.on('execution-plan:update', handlePlanUpdate);

            // Cleanup
            return () => {
                socket.off('execution-plan:update', handlePlanUpdate);
                if (workspaceId) {
                    socketService.leaveWorkspace(workspaceId);
                }
            };
        } else {
            console.warn('[LiveExecutionPlanProgress] Socket.io not available, falling back to polling');

            // Fallback to polling if Socket.io not available
            // const pollInterval = setInterval(loadExecution, 2000);
            // return () => clearInterval(pollInterval);
        }
    }, [id, navigate]);

    // Separate effect for elapsed time counter
    useEffect(() => {
        const timeInterval = setInterval(() => {
            setElapsedTime(prev => {
                if (execution?.startedAt) {
                    const now = new Date();
                    const start = new Date(execution.startedAt);
                    return Math.max(0, Math.floor((now - start) / 1000));
                }
                return prev + 1;
            });
        }, 1000);

        return () => clearInterval(timeInterval);
    }, [execution?.startedAt]);

    const loadExecution = async () => {
        try {
            const response = await executionPlanApi.getExecutionStatus(id);
            const data = response.data;

            setExecution(data.execution);
            setGoalExecutions(data.execution?.goalExecutions || []);
            setLoading(false);
        } catch (err) {
            console.error('Failed to load execution:', err);
            setError('Failed to load execution status');
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        setShowCancelDialog(true);
    };

    const confirmCancel = async () => {
        try {
            await executionPlanApi.cancelExecution(id);
            window.location.reload();
        } catch (err) {
            console.error('Failed to cancel execution:', err);
            setErrorMessage('Failed to cancel execution. Please try again.');
            setShowErrorDialog(true);
        }
    };

    const getStatusCounts = () => {
        const counts = {
            success: 0,
            failed: 0,
            running: 0,
            pending: 0,
            skipped: 0,
            inconclusive: 0,
            error: 0
        };

        goalExecutions.forEach(goal => {
            if (goal.status === 'success' || goal.status === 'passed') counts.success++;
            else if (goal.status === 'failed' || goal.status === 'error') counts.failed++;
            else if (goal.status === 'running') counts.running++;
            else if (goal.status === 'skipped') counts.skipped++;
            else if (goal.status === 'inconclusive') counts.inconclusive++;
            else if (goal.status === 'error') counts.error++;
            // else counts.pending++;
        });
        counts.pending = execution.totalGoals - goalExecutions.length;
        return counts;
    };

    const getProgressPercentage = () => {
        if (!execution.totalGoals) return 0;
        const completed = goalExecutions.filter(g =>
            ['success', 'failed', 'error', 'inconclusive', 'skipped', 'passed', 'error'].includes(g.status)
        ).length;
        return Math.round((completed / execution.totalGoals) * 100);
    };

    const formatElapsedTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const formatDuration = (ms) => {
        if (!ms) return '-';
        const seconds = Math.floor(ms / 1000);
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success':
            case 'passed':
                return '✓';
            case 'failed':
            case 'error':
                return '✗';
            case 'running':
                return '⟳';
            default:
                return '○';
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            success: { label: 'SUCCESS', className: 'live-execution-plan__badge--success' },
            passed: { label: 'SUCCESS', className: 'live-execution-plan__badge--success' },
            failed: { label: 'FAILED', className: 'live-execution-plan__badge--failed' },
            error: { label: 'ERROR', className: 'live-execution-plan__badge--error' },
            running: { label: 'RUNNING', className: 'live-execution-plan__badge--running' },
            queued: { label: 'PENDING', className: 'live-execution-plan__badge--pending' },
            pending: { label: 'PENDING', className: 'live-execution-plan__badge--pending' },
            inconclusive: { label: 'INCONCLUSIVE', className: 'badge-purple' },
            skipped: { label: 'SKIPPED', className: 'live-execution-plan__badge--skipped' }
        };
        return badges[status] || badges.pending;
    };

    if (loading) {
        return (
            <div className="live-execution-plan">
                <div className="live-execution-plan__loading">
                    <div className="live-execution-plan__spinner"></div>
                    <p>Loading execution...</p>
                </div>
            </div>
        );
    }

    if (error || !execution) {
        return (
            <div className="live-execution-plan">
                <div className="live-execution-plan__error">
                    <h2>Error</h2>
                    <p>{error || 'Execution not found'}</p>
                    <button onClick={() => navigate('/execution-plans')}>
                        ← Back to Execution Plans
                    </button>
                </div>
            </div>
        );
    }

    const statusCounts = getStatusCounts();
    const progress = getProgressPercentage();
    const completedCount = statusCounts.success + statusCounts.failed + statusCounts.skipped + statusCounts.inconclusive;

    return (
        <div className="live-execution-plan">
            {/* Header */}
            <div className="live-execution-plan__header">
                <button
                    className="live-execution-plan__back-btn"
                    onClick={() => navigate('/execution-plans/' + execution.executionPlanId)}
                >
                    ← Back
                </button>
                <div className="live-execution-plan__title-section">
                    <h1>{execution.executionPlan?.name || 'Execution Plan'}</h1>
                    <span className={`live-execution-plan__status-badge live-execution-plan__status-badge--${execution.status}`}>
                        {execution.status?.toUpperCase()}
                    </span>
                </div>
                {execution.status === 'running' && (
                    <button
                        className="live-execution-plan__cancel-btn"
                        onClick={handleCancel}
                    >
                        ✕ Cancel
                    </button>
                )}
            </div>
            {execution.errorMessage != null && execution.errorMessage.length > 0 && (
                <div className="live-execution-plan__global-error">
                    <span className="live-execution-plan__global-error-icon">⚠</span>
                    <span className="live-execution-plan__global-error-text">{execution.errorMessage}</span>
                </div>
            )}
            {/* Progress Overview */}
            <div className="live-execution-plan__overview">
                <div className="live-execution-plan__progress-section">
                    <div className="live-execution-plan__progress-bar-container">
                        <div
                            className="live-execution-plan__progress-bar"
                            style={{ width: `${progress}%` }}
                        >
                            <span className="live-execution-plan__progress-text">{progress}%</span>
                        </div>
                    </div>
                    <p className="live-execution-plan__progress-label">
                        {completedCount} of {execution.totalGoals} goals completed
                    </p>
                </div>

                <div className="live-execution-plan__stats">
                    <div 
                        className={`live-execution-plan__stat live-execution-plan__stat--success ${statusFilter === 'success' ? 'live-execution-plan__stat--active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === 'success' ? 'all' : 'success')}
                    >
                        <span className="live-execution-plan__stat-icon">✓</span>
                        <span className="live-execution-plan__stat-label">Success: {statusCounts.success}</span>
                    </div>
                    <div 
                        className={`live-execution-plan__stat live-execution-plan__stat--failed ${statusFilter === 'failed' ? 'live-execution-plan__stat--active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed')}
                    >
                        <span className="live-execution-plan__stat-icon">✗</span>
                        <span className="live-execution-plan__stat-label">Failed: {statusCounts.failed}</span>
                    </div>
                    <div 
                        className={`live-execution-plan__stat live-execution-plan__stat--failed ${statusFilter === 'error' ? 'live-execution-plan__stat--active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === 'error' ? 'all' : 'error')}
                    >
                        <span className="live-execution-plan__stat-icon">✗</span>
                        <span className="live-execution-plan__stat-label">Error: {statusCounts.error}</span>
                    </div>
                    <div 
                        className={`live-execution-plan__stat live-execution-plan__stat--inconclusive ${statusFilter === 'inconclusive' ? 'live-execution-plan__stat--active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === 'inconclusive' ? 'all' : 'inconclusive')}
                    >
                        <span className="live-execution-plan__stat-icon">✗</span>
                        <span className="live-execution-plan__stat-label">Inconclusive: {statusCounts.inconclusive}</span>
                    </div>
                    <div 
                        className={`live-execution-plan__stat live-execution-plan__stat--running ${statusFilter === 'running' ? 'live-execution-plan__stat--active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === 'running' ? 'all' : 'running')}
                    >
                        <span className="live-execution-plan__stat-icon">⟳</span>
                        <span className="live-execution-plan__stat-label">Running: {statusCounts.running}</span>
                    </div>
                    <div 
                        className={`live-execution-plan__stat live-execution-plan__stat--pending ${statusFilter === 'pending' ? 'live-execution-plan__stat--active' : ''}`}
                        onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
                    >
                        <span className="live-execution-plan__stat-icon">○</span>
                        <span className="live-execution-plan__stat-label">Pending: {statusCounts.pending}</span>
                    </div>
                </div>

                <div className="live-execution-plan__footer-row">
                    <div className="live-execution-plan__time">
                        <span className="live-execution-plan__time-icon">⏱</span>
                        {execution.status === 'running' ? (
                            <span>Elapsed time: {formatElapsedTime(elapsedTime)}</span>
                        ) : (
                            <span>Duration: {formatDuration(
                                execution.duration
                                    ? execution.duration
                                    : 0
                            )}</span>
                        )}
                    </div>
                    <div className="live-execution-plan__mode">
                        <span className="live-execution-plan__mode-icon">▶</span>
                        <span>Execution Mode: {execution.executionPlan?.executionMode}</span>
                    </div>
                </div>
            </div>

            {/* Execution Mode */}
            {/* <div className="live-execution-plan__mode">
                <span className="live-execution-plan__mode-icon">▶</span>
                <span>Execution Mode: {execution.executionPlan?.executionMode}</span>
            </div> */}

            {/* Goals List */}
            <div className="live-execution-plan__goals">
                {goalExecutions
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .filter(goal => {
                        if (statusFilter === 'all') return true;
                        if (statusFilter === 'success') return goal.status === 'success' || goal.status === 'passed';
                        if (statusFilter === 'failed') return goal.status === 'failed';
                        if (statusFilter === 'error') return goal.status === 'error';
                        return goal.status === statusFilter;
                    })
                    .map((goal, index) => {
                        const badge = getStatusBadge(goal.status);
                        const isRunning = goal.status === 'running';
                        const isCompleted = ['passed', 'success', 'failed', 'error', 'inconclusive'].includes(goal.status);

                        const errorMessage = goal.errorMessage;

                        return (
                            <div
                                key={goal.id}
                                className={`live-execution-plan__goal ${isRunning ? 'live-execution-plan__goal--running' : ''}`}
                            >
                                <div className="live-execution-plan__goal-header">
                                    <div className="live-execution-plan__goal-content">
                                        <span className="live-execution-plan__goal-index">{index + 1}.</span>
                                        <span className={`live-execution-plan__goal-icon live-execution-plan__goal-icon--${goal.status}`}>
                                            {getStatusIcon(goal.status)}
                                        </span>
                                        <span className="live-execution-plan__goal-text">
                                            {goal.testGoal?.goalText || goal.goalText}
                                        </span>
                                    </div>
                                    <div className="live-execution-plan__goal-status">
                                        <span className={`live-execution-plan__badge ${badge.className}`}>
                                            {badge.label}
                                        </span>
                                        <span className="live-execution-plan__goal-duration">
                                            {isRunning ? 'Running...' : formatDuration(goal.duration)}
                                        </span>
                                        {(isCompleted || isRunning) && goal.testExecutionId && (
                                            <Link
                                                to={`/test/results/${goal.testExecutionId}`}
                                                className="live-execution-plan__view-details"
                                            >
                                                View Details
                                            </Link>
                                        )}
                                    </div>
                                </div>
                                {errorMessage && (
                                    <div className="live-execution-plan__goal-error">
                                        <span className="live-execution-plan__goal-error-icon">⚠</span>
                                        <span className="live-execution-plan__goal-error-text">{errorMessage}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                isOpen={showCancelDialog}
                onClose={() => setShowCancelDialog(false)}
                title="Cancel Execution"
                message="Are you sure you want to cancel this execution? This action cannot be undone."
                type="confirm"
                variant="danger"
                confirmText="Yes, Cancel"
                cancelText="No, Keep Running"
                onConfirm={confirmCancel}
            />

            {/* Error Dialog */}
            <Dialog
                isOpen={showErrorDialog}
                onClose={() => setShowErrorDialog(false)}
                title="Error"
                message={errorMessage}
                type="alert"
                variant="danger"
                confirmText="OK"
            />
        </div>
    );
}

export default LiveExecutionPlanProgress;
